import { signOut as firebaseSignOut } from "firebase/auth";
import { useEffect } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { auth } from "../../Firebase";
import { signinSilentWithLock } from "../../utils/oidcSession";

export function OidcSessionBridge() {
    const oidc = useAuth();

    // Safety net: if OIDC drops the user without us initiating a redirect (e.g.
    // background SSO sign-out), clear Firebase too so cached state stays consistent.
    useEffect(() => {
        if (oidc.isLoading) {
            return;
        }
        if (oidc.isAuthenticated) {
            return;
        }
        if (oidc.activeNavigator) {
            return;
        }
        if (auth.currentUser) {
            void firebaseSignOut(auth);
        }
    }, [oidc.isLoading, oidc.isAuthenticated, oidc.activeNavigator]);

    useEffect(() => {
        return subscribeOidcEvents(oidc);
    }, [oidc]);

    return null;
}

// Most renew failures are transient network blips where the RT is still
// valid; redirecting on the first failure makes them a visible page reload.
// Three seconds clears most blips and still finishes well before the 60s
// expiring window runs out.
const RENEW_RETRY_DELAY_MS = 3000;

function getReturnTo(): string {
    return window.location.pathname + window.location.search + window.location.hash;
}

async function handleExpiring(oidc: AuthContextProps): Promise<void> {
    try {
        await signinSilentWithLock(oidc);
        return;
    } catch (firstErr) {
        console.warn("OIDC silent renew failed; retrying once.", firstErr);
    }

    try {
        await new Promise((resolve) => setTimeout(resolve, RENEW_RETRY_DELAY_MS));
        await signinSilentWithLock(oidc);
        return;
    } catch (retryErr) {
        console.error(
            "OIDC silent renew retry failed; redirecting through Keycloak.",
            retryErr,
        );
    }

    // Both refresh attempts failed: RT is likely dead. A top-level redirect
    // uses first-party cookies, so a still-valid SSO session re-auths
    // without showing the login page.
    try {
        await oidc.signinRedirect({ state: { returnTo: getReturnTo() } });
    } catch (redirectErr) {
        console.error(
            "OIDC signinRedirect after silent renew failure failed.",
            redirectErr,
        );
    }
}

/**
 * Keeps Firebase in sync with Keycloak OIDC state and drives access-token
 * renew. `automaticSilentRenew` is disabled on AuthProvider so proactive
 * renewals funnel through `handleExpiring`, which retries one transient
 * failure before redirecting. Silent renewals are serialized by
 * `signinSilentWithLock`.
 */
function subscribeOidcEvents(oidc: AuthContextProps): () => void {
    return oidc.events.addAccessTokenExpiring(() => {
        void handleExpiring(oidc);
    });
}
